import { useCallback, useEffect, useState } from 'react'
import { useNavigate, useRouterState } from '@tanstack/react-router'
import { Loader } from '@/components/common/Loader'
import { LiffErrorScreen } from '@/components/liff/LiffErrorScreen'
import {
  initLiff,
  isLiffInClient,
  isLiffLoggedIn,
  LiffInitError,
  resolveLiffProfile,
} from '@/lib/liff'
import {
  checkLiffBinding,
  hasAuthTokens,
  liffSignIn,
} from '@/modules/auth/service/liff'

type BootstrapState = 'loading' | 'ready' | 'error'

export function LiffBootstrap({ children }: { children: React.ReactNode }) {
  const navigate = useNavigate()
  const pathname = useRouterState({ select: (s) => s.location.pathname })
  const isLoginRoute = pathname === '/liff/login' || pathname.endsWith('/liff/login')
  const [state, setState] = useState<BootstrapState>(isLoginRoute ? 'ready' : 'loading')
  const [errorMessage, setErrorMessage] = useState('')
  const [retryKey, setRetryKey] = useState(0)

  const runBootstrap = useCallback(async () => {
    if (isLoginRoute) {
      setState('ready')
      return
    }

    setState('loading')
    setErrorMessage('')

    try {
      await initLiff()

      if (hasAuthTokens()) {
        setState('ready')
        return
      }

      // Wait until LINE login completes (avoid calling liff.login during init loop)
      if (isLiffInClient() && !isLiffLoggedIn()) {
        navigate({ to: '/liff/login' })
        return
      }

      const profile = await resolveLiffProfile()
      const binding = await checkLiffBinding(profile)

      if (binding.bound) {
        await liffSignIn(profile)
        setState('ready')
        return
      }

      navigate({ to: '/liff/login' })
    } catch (err) {
      if (hasAuthTokens()) {
        setState('ready')
        return
      }

      if (err instanceof LiffInitError) {
        setErrorMessage(err.message)
        setState('error')
        return
      }

      // API/network errors — still allow manual login
      if (isLiffInClient() && !isLiffLoggedIn()) {
        navigate({ to: '/liff/login' })
        return
      }

      const msg = err instanceof Error ? err.message : 'เกิดข้อผิดพลาด'
      if (msg === 'LIFF_NOT_LOGGED_IN') {
        navigate({ to: '/liff/login' })
        return
      }

      setErrorMessage(msg)
      setState('error')
    }
  }, [isLoginRoute, navigate])

  useEffect(() => {
    void runBootstrap()
  }, [runBootstrap, retryKey])

  if (state === 'loading') {
    return <Loader.FullScreen />
  }

  if (state === 'error') {
    return (
      <LiffErrorScreen
        message={errorMessage || 'ไม่สามารถโหลด LINE Mini App ได้'}
        onRetry={() => setRetryKey((k) => k + 1)}
      />
    )
  }

  return <>{children}</>
}
